import React, { useEffect, useMemo, useState } from 'react';
import MapboxMapWrapper from './MapboxMapWrapper';
import {
  formatStoredDeliveryLabel,
  hasUserDeliveryLocation,
  loadHomeDeliveryLocation,
  StoredDeliveryLocation,
} from '../utils/homeLocationStorage';
import { formatListingPrice } from '../utils/listingPricing';
import { Listing } from '../api';

const NAVY = '#1e293b';
const MUTED = '#64748b';
const BORDER = '#e2e8f0';
const FOOTER_BG = '#f8fafc';
const YOU_BLUE = '#3b82f6';
const WARN = '#b45309';
const WARN_BG = '#fffbeb';
const WARN_BORDER = '#fde68a';

const MAP_HEIGHT = 320;

interface DeliveryAreaCardProps {
  listing: Listing;
  sellerAddress: string | null;
  sellerCoords: [number, number] | null;
  mapLoading?: boolean;
  maxMiles: number | null;
  deliveryTime: string | null;
  distanceMiles: number | null;
  actualDeliveryFee: number | null;
  feeLines: string[] | null;
  isSold?: boolean;
  onChangeLocation: () => void;
}

function buyerCityLabel(stored: StoredDeliveryLocation | null): string {
  if (!stored) return 'Set delivery location';
  const city = (stored.city || '').trim();
  const state = (stored.state || '').trim();
  const zip = (stored.zip || '').trim();
  if (city && state) return `${city}, ${state}${zip ? ` ${zip}` : ''}`;
  if (city && zip) return `${city} ${zip}`;
  const label = (stored.label || '').trim();
  if (label) {
    const parts = label.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const cityPart = parts[parts.length - 2];
      const stateZip = parts[parts.length - 1];
      if (/^[A-Z]{2}\b/i.test(stateZip) || /\d{5}/.test(stateZip)) {
        return `${cityPart}, ${stateZip}`;
      }
    }
  }
  if (zip) return zip;
  return 'Set delivery location';
}

/** Street number + name for compact footers (e.g. "123 Main St"). */
function buyerStreetLabel(stored: StoredDeliveryLocation | null): string {
  if (!stored) return 'Set delivery location';
  const fromHelpers = formatStoredDeliveryLabel(stored).trim();
  if (fromHelpers) return fromHelpers;
  return buyerCityLabel(stored);
}

/** Full delivery address for the details sheet (street + city, state zip). */
function buyerFullAddress(stored: StoredDeliveryLocation | null): string {
  if (!stored) return 'Set delivery location';
  const label = (stored.label || '').trim();
  const street = label ? label.split(',')[0]?.trim() || label : '';
  const city = (stored.city || '').trim();
  const state = (stored.state || '').trim();
  const zip = (stored.zip || '').trim();

  let cityStateZip = '';
  if (city && state) {
    cityStateZip = `${city}, ${state}${zip ? ` ${zip}` : ''}`;
  } else if (city && zip) {
    cityStateZip = `${city} ${zip}`;
  } else if (zip) {
    cityStateZip = zip;
  } else if (label) {
    // Label may already include city/state/zip after the street.
    const rest = label
      .split(',')
      .slice(1)
      .map((p) => p.trim())
      .filter(Boolean)
      .join(', ');
    if (rest) cityStateZip = rest;
  }

  if (street && cityStateZip) {
    // Avoid duplicating if street already embeds city/state/zip somehow.
    if (street.toLowerCase().includes(city.toLowerCase()) && city) return street;
    return `${street}, ${cityStateZip}`;
  }
  if (street) return street;
  if (cityStateZip) return cityStateZip;
  return 'Set delivery location';
}

/** Street line + city/state/zip for the details sheet layout. */
function buyerAddressLines(stored: StoredDeliveryLocation | null): {
  street: string | null;
  cityStateZip: string | null;
  full: string;
} {
  const full = buyerFullAddress(stored);
  if (!stored || full === 'Set delivery location') {
    return { street: null, cityStateZip: null, full };
  }
  const label = (stored.label || '').trim();
  const street = (label ? label.split(',')[0]?.trim() : '') || null;
  const city = (stored.city || '').trim();
  const state = (stored.state || '').trim();
  const zip = (stored.zip || '').trim();
  let cityStateZip: string | null = null;
  if (city && state) cityStateZip = `${city}, ${state}${zip ? ` ${zip}` : ''}`;
  else if (city && zip) cityStateZip = `${city} ${zip}`;
  else if (zip) cityStateZip = zip;
  else if (label.includes(',')) {
    const rest = label
      .split(',')
      .slice(1)
      .map((p) => p.trim())
      .filter(Boolean)
      .join(', ');
    cityStateZip = rest || null;
  }
  return { street: street || null, cityStateZip, full };
}

function formatEta(deliveryTime: string | null, listing: Listing): string {
  let core = (deliveryTime || '').trim();
  if (!core) {
    switch (listing.delivery_time_option) {
      case '1-2':
        core = '1–2 hours';
        break;
      case '3-6':
        core = '3–6 hours';
        break;
      case '24':
        core = 'Within 24 hours';
        break;
      default:
        core = '30–45 min';
    }
  }
  const lower = core.toLowerCase();
  if (lower.startsWith('est.')) return core;
  if (lower.startsWith('eta')) {
    return `Est. ${core.replace(/^eta[:\s]*/i, '').trim()}`;
  }
  return `Est. ${core}`;
}

function summaryFeeLabel(actualDeliveryFee: number | null, outside: boolean): string | null {
  if (outside) return null;
  if (actualDeliveryFee != null && Number.isFinite(actualDeliveryFee) && actualDeliveryFee >= 0) {
    return actualDeliveryFee === 0 ? 'Free' : formatListingPrice(actualDeliveryFee);
  }
  return null;
}

function formatDistanceAway(miles: number): string {
  if (miles < 0.1) return 'Less than 0.1 mi';
  if (miles < 10) return `${miles.toFixed(1)} mi`;
  return `${Math.round(miles)} mi`;
}

function isOutsideArea(distanceMiles: number | null, maxMiles: number | null): boolean {
  if (distanceMiles == null || maxMiles == null || maxMiles <= 0) return false;
  return distanceMiles > maxMiles + 0.05;
}

const DeliveryAreaCard: React.FC<DeliveryAreaCardProps> = ({
  listing,
  sellerAddress,
  sellerCoords,
  mapLoading = false,
  maxMiles,
  deliveryTime,
  distanceMiles,
  actualDeliveryFee,
  feeLines,
  isSold = false,
  onChangeLocation,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [locationTick, setLocationTick] = useState(0);

  useEffect(() => {
    const onUpdate = () => setLocationTick((t) => t + 1);
    window.addEventListener('delivery-location-updated', onUpdate);
    return () => window.removeEventListener('delivery-location-updated', onUpdate);
  }, []);

  const hasBuyer = hasUserDeliveryLocation();
  const stored = hasBuyer ? loadHomeDeliveryLocation() : null;
  void locationTick;
  const footerBuyerLabel = buyerStreetLabel(stored);
  const detailsAddress = buyerAddressLines(stored);
  const buyerLat = stored?.lat;
  const buyerLng = stored?.lng;

  const outside = isOutsideArea(distanceMiles, maxMiles);
  const etaLabel = formatEta(deliveryTime, listing);
  const feeLabel = summaryFeeLabel(actualDeliveryFee, outside);
  const hasCalculatedDistance = hasBuyer && distanceMiles != null && Number.isFinite(distanceMiles);

  const locationSubtitle = (() => {
    if (outside) return 'Outside delivery area';
    if (!hasBuyer) return 'Add where we should deliver';
    return null;
  })();

  // Distance + delivery fee under ETA (not under the address).
  const footerMeta = useMemo(() => {
    if (!hasCalculatedDistance || outside) return '';
    const parts: string[] = [];
    parts.push(formatDistanceAway(distanceMiles!));
    if (feeLabel) {
      parts.push(feeLabel === 'Free' ? 'Free delivery fee' : `${feeLabel} delivery fee`);
    }
    return parts.join(' · ');
  }, [hasCalculatedDistance, outside, distanceMiles, feeLabel]);

  const markers = useMemo(() => {
    const list: Array<{
      position: [number, number];
      content: React.ReactNode;
      anchor?: 'center' | 'bottom';
    }> = [];
    if (sellerCoords) {
      list.push({
        position: sellerCoords,
        anchor: 'center',
        content: (
          <div
            className="flex h-9 w-9 items-center justify-center rounded-full bg-green-600 shadow-md ring-2 ring-white"
            aria-label="Seller"
          >
            <i className="ri-car-fill text-lg leading-none text-white" aria-hidden />
          </div>
        ),
      });
    }
    if (
      buyerLat != null &&
      buyerLng != null &&
      Number.isFinite(buyerLat) &&
      Number.isFinite(buyerLng)
    ) {
      list.push({
        position: [buyerLat, buyerLng],
        anchor: 'center',
        content: (
          <div
            className="h-3.5 w-3.5 rounded-full border-2 border-white shadow"
            style={{ backgroundColor: YOU_BLUE }}
            aria-label="Your location"
          />
        ),
      });
    }
    return list;
  }, [sellerCoords, buyerLat, buyerLng]);

  const mapAvailable = !!(sellerCoords || (sellerAddress && sellerAddress.trim()));

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="p-5 sm:p-6">
        <div className="mb-3 flex items-start gap-2.5">
          <i className="ri-truck-line mt-0.5 text-xl text-green-600" aria-hidden />
          <div className="min-w-0">
            <h3 className="text-[17px] font-bold leading-tight" style={{ color: NAVY }}>
              Delivery Area
            </h3>
            <p className="mt-0.5 text-[13px] leading-snug" style={{ color: MUTED }}>
              Seller delivers to your location within the shaded area.
            </p>
          </div>
        </div>

        {mapLoading ? (
          <div
            className="rounded-[14px] border bg-gray-100"
            style={{ borderColor: BORDER, height: MAP_HEIGHT }}
            aria-busy="true"
            aria-label="Loading map"
          />
        ) : mapAvailable && sellerCoords ? (
          <div className="relative overflow-hidden rounded-[14px] border" style={{ borderColor: BORDER }}>
            <MapboxMapWrapper
              center={sellerCoords}
              radiusMiles={maxMiles}
              markers={markers}
              height={`${MAP_HEIGHT}px`}
              interactive={true}
              scrollZoom={true}
              className="rounded-[14px]"
              style={{ width: '100%', height: MAP_HEIGHT, minHeight: MAP_HEIGHT }}
            />

            <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between gap-2 p-3">
              <div
                className="pointer-events-auto flex max-w-[75%] items-center gap-2.5 rounded-2xl border bg-white px-3 py-2.5 shadow-sm"
                style={{ borderColor: BORDER }}
              >
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-600"
                  aria-hidden
                >
                  <i className="ri-time-fill text-sm text-white" />
                </span>
                <div className="min-w-0">
                  {outside ? (
                    <p className="truncate text-[13px] font-semibold" style={{ color: WARN }}>
                      Outside delivery area
                    </p>
                  ) : !hasBuyer ? (
                    <>
                      <p className="truncate text-[13px] font-semibold" style={{ color: NAVY }}>
                        {etaLabel}
                      </p>
                      <p className="truncate text-[11px]" style={{ color: MUTED }}>
                        Add where we should deliver
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="truncate text-[13px] font-semibold" style={{ color: NAVY }}>
                        {etaLabel}
                      </p>
                      {hasCalculatedDistance && (
                        <p className="truncate text-[11px]" style={{ color: MUTED }}>
                          {formatDistanceAway(distanceMiles!)} away
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={onChangeLocation}
                className="pointer-events-auto hidden shrink-0 items-center gap-1.5 rounded-lg bg-green-600 px-3 py-2 text-[13px] font-semibold text-white shadow-sm hover:bg-green-700 sm:inline-flex"
              >
                <i className="ri-map-pin-line text-sm" aria-hidden />
                Change
              </button>
            </div>
          </div>
        ) : (
          <div
            className="flex h-[120px] items-center justify-center rounded-[14px] border text-sm"
            style={{ borderColor: BORDER, backgroundColor: FOOTER_BG, color: MUTED }}
          >
            Map unavailable
          </div>
        )}

        {outside && (
          <div
            className="mt-3 flex items-start gap-2.5 rounded-xl border p-3"
            style={{ backgroundColor: WARN_BG, borderColor: WARN_BORDER }}
          >
            <i className="ri-error-warning-fill mt-0.5 text-base" style={{ color: WARN }} aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold" style={{ color: NAVY }}>
                Outside delivery area
              </p>
              <p className="mt-0.5 text-[11px]" style={{ color: MUTED }}>
                {maxMiles != null
                  ? `This seller delivers up to ${maxMiles} mi. Change your location or message the seller.`
                  : 'This location is outside the seller’s delivery range.'}
              </p>
            </div>
            <button
              type="button"
              onClick={onChangeLocation}
              className="shrink-0 text-[13px] font-semibold text-green-600 hover:text-green-700"
            >
              Change
            </button>
          </div>
        )}
      </div>

      {!isSold && (
        <button
          type="button"
          onClick={() => setShowDetails(true)}
          className="flex w-full items-center gap-2.5 border-t px-4 py-3 text-left hover:bg-gray-50 sm:px-5"
          style={{ backgroundColor: FOOTER_BG, borderColor: BORDER }}
        >
          <i className="ri-map-pin-2-fill shrink-0 text-base text-green-600" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold" style={{ color: NAVY }}>
              {footerBuyerLabel}
            </p>
            {locationSubtitle && (
              <p className="truncate text-[11px]" style={{ color: outside ? WARN : MUTED }}>
                {locationSubtitle}
              </p>
            )}
          </div>
          <div className="shrink-0 text-right">
            <div className="flex items-center justify-end gap-1.5">
              <i className="ri-truck-line text-base text-green-600" aria-hidden />
              <p className="text-[13px] font-semibold" style={{ color: NAVY }}>
                {etaLabel}
              </p>
            </div>
            {footerMeta && (
              <p className="mt-0.5 text-[11px]" style={{ color: MUTED }}>
                {footerMeta}
              </p>
            )}
          </div>
          <i
            className="ri-arrow-right-s-line shrink-0 text-lg"
            style={{ color: NAVY, opacity: 0.55 }}
            aria-hidden
          />
        </button>
      )}

      {showDetails && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-label="Delivery details"
          onClick={() => setShowDetails(false)}
        >
          <div
            className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h4 className="text-[15px] font-semibold" style={{ color: NAVY }}>
                Delivery details
              </h4>
              <button
                type="button"
                onClick={() => setShowDetails(false)}
                className="text-[13px] font-semibold text-green-600 hover:text-green-700"
              >
                Done
              </button>
            </div>

            <section className="mb-4">
              <p className="mb-1 text-[13px] font-semibold" style={{ color: NAVY }}>
                Delivering to
              </p>
              {detailsAddress.street && detailsAddress.cityStateZip ? (
                <>
                  <p className="text-sm" style={{ color: NAVY }}>
                    {detailsAddress.street}
                  </p>
                  <p className="text-sm" style={{ color: NAVY }}>
                    {detailsAddress.cityStateZip}
                  </p>
                </>
              ) : (
                <p className="text-sm" style={{ color: NAVY }}>
                  {detailsAddress.full}
                </p>
              )}
              {locationSubtitle && (
                <p className="mt-0.5 text-xs" style={{ color: MUTED }}>
                  {locationSubtitle}
                </p>
              )}
              <button
                type="button"
                onClick={() => {
                  setShowDetails(false);
                  onChangeLocation();
                }}
                className="mt-2 text-[13px] font-semibold text-green-600 hover:text-green-700"
              >
                Change location
              </button>
            </section>

            <section className="mb-4">
              <p className="mb-1 text-[13px] font-semibold" style={{ color: NAVY }}>
                Estimated time
              </p>
              <p className="text-sm" style={{ color: NAVY }}>
                {etaLabel}
              </p>
            </section>

            {(hasCalculatedDistance || maxMiles != null || (feeLines && feeLines.length > 0)) && (
              <section className="mb-4">
                <p className="mb-1 text-[13px] font-semibold" style={{ color: NAVY }}>
                  Range & fee
                </p>
                {hasCalculatedDistance && !outside && (
                  <p className="text-sm" style={{ color: NAVY }}>
                    {[
                      formatDistanceAway(distanceMiles!),
                      feeLabel
                        ? feeLabel === 'Free'
                          ? 'Free delivery fee'
                          : `${feeLabel} delivery fee`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                )}
                {outside && maxMiles != null && (
                  <p className="text-sm" style={{ color: NAVY }}>
                    Outside range · seller delivers up to {maxMiles} mi
                  </p>
                )}
                {!hasCalculatedDistance && maxMiles != null && (
                  <p className="text-sm" style={{ color: MUTED }}>
                    Seller delivers up to {maxMiles} mi — set your address to see your fee
                  </p>
                )}
                {feeLines && feeLines.length > 0 && (
                  <ul className="mt-2 space-y-0.5">
                    {feeLines.map((line) => (
                      <li key={line} className="text-xs" style={{ color: MUTED }}>
                        {line}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {sellerAddress && (
              <section>
                <p className="mb-1 text-[13px] font-semibold" style={{ color: NAVY }}>
                  Seller area
                </p>
                <p className="text-sm" style={{ color: MUTED }}>
                  {sellerAddress}
                </p>
              </section>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryAreaCard;
